'use client';

import { useEffect, useState } from 'react';
import { Link } from '@/i18n/routing';
import { useTranslations } from '@/i18n/intl';
import Image from '@/components/shared/Image';
import { getSettings } from '@/lib/actions/settings';
import { RichText } from '@/components/shared/RichText';

export function HeroSection() {
  const t = useTranslations('Hero');
  const tCommon = useTranslations('Common');
  const [headline, setHeadline] = useState<string | undefined>();
  const [subheadline, setSubheadline] = useState<string | undefined>();

  useEffect(() => {
    let cancelled = false;
    getSettings(['home_hero_headline', 'home_hero_description']).then((settings) => {
      if (cancelled) return;
      setHeadline(settings.home_hero_headline);
      setSubheadline(settings.home_hero_description);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0">
        <Image
          src="/images/simisi/page1_img1.png"
          alt="Simisi Hero Background"
          fill
          priority
          sizes="100vw"
          className="object-cover object-center"
        />
      </div>
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-navy/75" />

      {/* Content */}
      <div className="relative z-10 max-w-[1170px] mx-auto px-5 md:px-[40px] w-full" style={{ paddingTop: '10rem', paddingBottom: '10rem' }}>
        <div className="max-w-[800px]">
          {headline ? (
            <RichText
              as="h1"
              html={headline}
              className="text-[44px] sm:text-[52px] md:text-[64px] lg:text-[72px] font-bold leading-[1.15] tracking-[-0.035em] font-heading text-white overflow-visible [&_p]:text-[44px] sm:[&_p]:text-[52px] md:[&_p]:text-[64px] lg:[&_p]:text-[72px] [&_h2]:text-[44px] sm:[&_h2]:text-[52px] md:[&_h2]:text-[64px] lg:[&_h2]:text-[72px] [&_h3]:text-[44px] sm:[&_h3]:text-[52px] md:[&_h3]:text-[64px] lg:[&_h3]:text-[72px] [&_p]:mb-4 [&_p]:font-bold [&_p]:leading-[1.15] [&_p]:flex [&_p]:flex-col [&_p]:md:block [&_span]:flex [&_span]:items-center [&_span]:bg-primary [&_span]:px-3 [&_span]:md:px-4 [&_span]:py-[0.1em] [&_span]:rounded-2xl [&_span]:shadow-[0_10px_30px_rgba(13,91,255,0.35)] [&_span]:leading-none [&_span]:w-fit [&_span]:mt-2 [&_span]:md:mt-0 md:[&_span]:inline-flex [&_a]:text-white"
            />
          ) : (
            <h1 className="text-[44px] sm:text-[52px] md:text-[64px] lg:text-[72px] font-bold leading-[1.15] tracking-[-0.035em] font-heading text-white overflow-visible">
              {t.rich('headline', {
                l1: (chunks) => <span className="block mb-2">{chunks}</span>,
                l2: (chunks) => (
                  <span className="flex flex-col md:flex-row md:items-baseline gap-x-2 md:gap-x-3 w-fit">
                    {chunks}
                  </span>
                ),
                gold: (chunks) => (
                  <span className="flex items-center bg-primary px-3 md:px-4 py-[0.1em] rounded-2xl shadow-[0_10px_30px_rgba(13,91,255,0.35)] leading-none w-fit mt-2 md:mt-0 md:inline-flex">
                    {chunks}
                  </span>
                ),
              })}
            </h1>
          )}

          {subheadline ? (
            <RichText
              html={subheadline}
              className="text-[15px] sm:text-[16px] md:text-[18px] text-white font-normal mt-[25px] mb-10 max-w-[500px] leading-[1.56] font-sans [&_a]:text-white"
            />
          ) : (
            <p className="text-[15px] sm:text-[16px] md:text-[18px] text-white font-normal mt-[25px] mb-10 max-w-[500px] leading-[1.56] font-sans">
              {t('subheadline')}
            </p>
          )}
          
          <div className="flex flex-col sm:flex-row items-start gap-4">
            <Link
              href="/#about-us"
              className="inline-flex items-center bg-primary text-white text-[14px] font-semibold uppercase px-[25px] py-[11px] rounded-[25px] hover:bg-primary/90 transition-all duration-300"
            >
              {tCommon('readMore')}
            </Link>
            <Link
              href="/contact-us"
              className="inline-flex items-center bg-white text-navy text-[14px] font-semibold uppercase px-[25px] py-[11px] rounded-[25px] hover:bg-primary hover:text-white transition-all duration-300"
            >
              {tCommon('contactUs')}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
